"use client";

import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { assetService } from '@/services/asset-service';

type AssetType = {
    name: string;
    label: string;
    description: string;
};

type Props = {
    assetTypes: AssetType[];
    onClose: () => void;
    onComplete: () => void;
};

// Filter types that are JSON/config based
const getConfigTypes = (types: AssetType[]): AssetType[] => {
    return types.filter(type =>
        type.name.includes('application/') && type.name.includes('json')
    );
};

export function ConfigCreationForm({ assetTypes, onClose, onComplete }: Props) {
    const [selectedType, setSelectedType] = useState<string>('');
    const [assetName, setAssetName] = useState('');
    const [description, setDescription] = useState('');
    const [jsonData, setJsonData] = useState('{}');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [jsonError, setJsonError] = useState<string | null>(null);

    const configTypes = getConfigTypes(assetTypes);
    const selectedTypeObj = configTypes.find(t => t.name === selectedType);

    // Validate JSON
    const validateJson = (json: string): boolean => {
        try {
            JSON.parse(json);
            setJsonError(null);
            return true;
        } catch (error) {
            setJsonError((error as Error).message);
            return false;
        }
    };

    const handleJsonChange = (value: string) => {
        setJsonData(value);
        validateJson(value);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validation
        if (!selectedType) {
            toast.error('Please select an asset type');
            return;
        }
        if (!assetName.trim()) {
            toast.error('Please enter an asset name');
            return;
        }
        if (!validateJson(jsonData)) {
            toast.error('Invalid JSON format');
            return;
        }

        setIsSubmitting(true);
        try {
            // Upload JSON content to R2 and create asset with URI reference
            // This follows the same pattern as file uploads - content is stored in R2, not in database
            const filename = 'config.json';
            await assetService.createAssetFromContent(
                jsonData,
                assetName.trim(),
                selectedType,
                filename
            );

            toast.success('Configuration asset created successfully');
            onComplete();
        } catch (error) {
            console.error('Error creating configuration asset:', error);
            toast.error((error as Error).message || 'Failed to create configuration asset');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
                {/* Asset Type Selection */}
                <div className="space-y-2">
                    <Label htmlFor="type-select">Asset Type</Label>
                    <Select value={selectedType} onValueChange={setSelectedType}>
                        <SelectTrigger id="type-select">
                            <SelectValue placeholder="Select configuration type" />
                        </SelectTrigger>
                        <SelectContent>
                            {configTypes.map(type => (
                                <SelectItem key={type.name} value={type.name}>
                                    {type.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {selectedTypeObj?.description && (
                        <p className="text-sm text-gray-500">{selectedTypeObj.description}</p>
                    )}
                </div>

                {/* Asset Name */}
                <div className="space-y-2">
                    <Label htmlFor="name-input">Asset Name</Label>
                    <Input
                        id="name-input"
                        placeholder="e.g., Agent Configuration"
                        value={assetName}
                        onChange={(e) => setAssetName(e.target.value)}
                    />
                </div>

                {/* Description */}
                <div className="space-y-2">
                    <Label htmlFor="description-input">Description (Optional)</Label>
                    <Textarea
                        id="description-input"
                        placeholder="Add notes about this configuration..."
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="min-h-20"
                    />
                </div>

                {/* JSON Editor */}
                <div className="space-y-2">
                    <Label htmlFor="json-input">Configuration JSON</Label>
                    <Textarea
                        id="json-input"
                        placeholder='{"key": "value"}'
                        value={jsonData}
                        onChange={(e) => handleJsonChange(e.target.value)}
                        className="font-mono text-sm min-h-48"
                    />
                    {jsonError && (
                        <Alert variant="destructive">
                            <AlertDescription>{jsonError}</AlertDescription>
                        </Alert>
                    )}
                </div>

                {/* Privacy Notice */}
                <Alert>
                    <AlertDescription>
                        🔒 <strong>Personal Asset:</strong> Only you can see this asset. You can share it with your organization later.
                    </AlertDescription>
                </Alert>
            </div>

            {/* Actions */}
            <div className="flex gap-2 justify-end">
                <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    disabled={isSubmitting || !selectedType || !assetName.trim() || jsonError !== null}
                >
                    {isSubmitting ? 'Creating...' : 'Create Asset'}
                </Button>
            </div>
        </form>
    );
}
